/**
 * StreamTest — verifies the live-streaming end-to-end flow over either WHEP
 * (WebRTC, sub-second latency, the live operator default) or LL-HLS (2-3s
 * latency, works behind firewalls that block UDP/8189, and the cost lever
 * for high-fan-out scenarios — see the Live Streaming Architecture page).
 *
 *   1. POST  /monitoring-api/streaming/stream-session/<camera_id>/  (Bearer)
 *      → { whep_url, hls_url, token, session_id, lookback_seconds, ... }
 *      The POST also writes the publish-on-demand lease: the connector only
 *      publishes this camera to MediaMTX while a lease exists.
 *   2a. WHEP: POST <whep_url>?jwt=<token>  with WebRTC SDP offer
 *       → SDP answer; ICE-UDP via NLB :8189; RTP/H.264 flows
 *   2b. HLS:  GET <hls_url>?jwt=<token>  via hls.js (low-latency mode)
 *       → MediaMTX serves the same H.264 stream as cacheable segments.
 *          hls.js keeps a client back-buffer (lookback_seconds from the
 *          session, per-customer entitlement) so you can pause / scrub back.
 *
 * Keepalive: while playing, the session POST is repeated every ~4 min,
 * echoing session_id. One mechanism, three jobs: re-mints the 5-min JWT
 * (long views no longer die at token expiry), renews the 10-min publish
 * lease (the connector keeps publishing while you watch), and ticks the
 * billing record (+4 min per keepalive). For LL-HLS the fresh token is
 * injected per-request via hls.js xhrSetup; WHEP needs no re-auth after ICE.
 *
 * Cold start: a camera nobody is watching isn't publishing. The first Play
 * writes the lease and the connector comes up in ~3-8s, so both players
 * retry through that window ("starting stream…") instead of failing.
 */
import React, { useEffect, useRef, useState } from "react";
import { createMonitoringAPICall } from "../../utils/createMonitoringAPICall";

interface StreamSessionResponse {
  whep_url: string;
  hls_url: string;
  token: string;
  expires_at: number;
  session_id: string | null;
  lookback_seconds?: number;
}

type Protocol = "whep" | "hls";

const KEEPALIVE_MS = 4 * 60 * 1000; // < both the 5-min JWT and 10-min lease
// A hidden tab would otherwise keepalive forever: lease keeps renewing, the
// camera keeps publishing, minutes keep metering — with nobody watching.
// After this long continuously in the background we release the stream.
const HIDDEN_AUTO_STOP_MS = 10 * 60 * 1000;
const START_RETRIES = 20; // × 2.5s ≈ 50s: covers a scale-from-zero streaming deployment wake, not just a warm connector cold start
const START_RETRY_DELAY_MS = 2500;

export const StreamTest: React.FC = () => {
  const [cameraId, setCameraId] = useState("testpattern");
  const [protocol, setProtocol] = useState<Protocol>("whep");
  const [logLines, setLogLines] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  // hls.js Hls instance; typed as any so this file doesn't depend on
  // @types/hls.js (the package ships types; we keep this loose to avoid
  // a hard build dep in case the package is added lazily).
  const hlsRef = useRef<any>(null);
  // Current JWT + billing session, refreshed by the keepalive. tokenRef is
  // read per-request by hls.js xhrSetup so a re-mint takes effect without
  // reloading the source.
  const tokenRef = useRef<string>("");
  const sessionIdRef = useRef<string | null>(null);
  const keepaliveTimerRef = useRef<any>(null);
  const retryTimerRef = useRef<any>(null);
  // De-dupe noisy non-fatal hls errors (a rough seek can emit a dozen
  // identical fragGap events in one second).
  const lastHlsErrRef = useRef<{ key: string; t: number; n: number }>({
    key: "",
    t: 0,
    n: 0,
  });
  // Periodic readout of the client-side buffer so we can see how much rewind
  // (back-buffer) is available and how far behind the live edge we are.
  const statsTimerRef = useRef<any>(null);
  const [bufInfo, setBufInfo] = useState("");
  // Background-tab auto-release + session/usage readout.
  const hiddenTimerRef = useRef<any>(null);
  const sessionStartRef = useRef<number>(0);
  const meterTicksRef = useRef<number>(0); // 1 per mint; each tick ≈ 4 metered min
  const [notice, setNotice] = useState("");

  const log = (msg: string) => {
    const stamp = new Date().toLocaleTimeString();
    setLogLines((prev) => [...prev, `${stamp}  ${msg}`]);
  };

  const stop = () => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
      log("peer connection closed");
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
      log("hls player destroyed");
    }
    if (keepaliveTimerRef.current) {
      clearInterval(keepaliveTimerRef.current);
      keepaliveTimerRef.current = null;
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (statsTimerRef.current) {
      clearInterval(statsTimerRef.current);
      statsTimerRef.current = null;
    }
    if (hiddenTimerRef.current) {
      clearTimeout(hiddenTimerRef.current);
      hiddenTimerRef.current = null;
    }
    // New Play = new distinct billing session.
    sessionIdRef.current = null;
    setBufInfo("");
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.removeAttribute("src");
      videoRef.current.load();
    }
  };

  useEffect(() => stop, []);

  // Auto-release on abandoned background tabs: when the document stays
  // hidden for HIDDEN_AUTO_STOP_MS while a stream is active, stop cleanly —
  // the lease lapses behind us (~11 min) and the camera's publisher shuts
  // off. Returning to the tab and pressing Play resumes in ~1s (the lease
  // is usually still warm). Production viewers should adopt this pattern.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        if (keepaliveTimerRef.current && !hiddenTimerRef.current) {
          hiddenTimerRef.current = setTimeout(() => {
            hiddenTimerRef.current = null;
            stop();
            setNotice(
              "⏹ stream released after 10 min in a background tab — press Play to resume"
            );
            log("⏹ auto-released: tab hidden 10 min (lease lapses; publisher stops)");
          }, HIDDEN_AUTO_STOP_MS);
        }
      } else if (hiddenTimerRef.current) {
        clearTimeout(hiddenTimerRef.current);
        hiddenTimerRef.current = null;
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs only
  }, []);

  const mintSession = async (camId: string): Promise<StreamSessionResponse> => {
    const api = createMonitoringAPICall("");
    const resp = await api.post<StreamSessionResponse>(
      `streaming/stream-session/${camId}/`,
      { session_id: sessionIdRef.current }
    );
    tokenRef.current = resp.data.token;
    sessionIdRef.current = resp.data.session_id ?? sessionIdRef.current;
    meterTicksRef.current += 1; // server adds STREAMING_METER_TICK (4 min)/mint
    return resp.data;
  };

  // Re-mint every ~4 min while playing: refreshes the 5-min JWT, renews the
  // 10-min publish lease, and ticks the billing record — one POST, three jobs.
  const startKeepalive = (camId: string) => {
    if (keepaliveTimerRef.current) clearInterval(keepaliveTimerRef.current);
    keepaliveTimerRef.current = setInterval(async () => {
      try {
        await mintSession(camId);
        log(
          `⟳ keepalive: token re-minted + lease renewed (session ${
            sessionIdRef.current ?? "-"
          })`
        );
      } catch (e: any) {
        log(`✗ keepalive failed: ${e?.response?.status ?? e?.message ?? e}`);
      }
    }, KEEPALIVE_MS);
  };

  // Surface the client buffer state: how far back you can scrub (rewind
  // available = currentTime − buffered.start) and how far behind live you are.
  const startBufferStats = () => {
    if (statsTimerRef.current) clearInterval(statsTimerRef.current);
    statsTimerRef.current = setInterval(() => {
      const v = videoRef.current;
      if (!v || v.buffered.length === 0) return;
      const start = v.buffered.start(0);
      const end = v.buffered.end(v.buffered.length - 1);
      const rewind = Math.max(0, v.currentTime - start);
      const behindLive = Math.max(0, end - v.currentTime);
      const sessionMin = sessionStartRef.current
        ? Math.floor((Date.now() - sessionStartRef.current) / 60000)
        : 0;
      setBufInfo(
        `${v.paused ? "⏸ paused" : "▶ playing"}  ·  session: ${sessionMin}m (metered ≈${
          meterTicksRef.current * 4
        }min)  ·  rewind available: ${rewind.toFixed(
          0
        )}s  ·  behind live: ${behindLive.toFixed(1)}s  ·  buffered window: ${(
          end - start
        ).toFixed(0)}s`
      );
    }, 500);
  };

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      retryTimerRef.current = setTimeout(resolve, ms);
    });

  const playWhep = async (whep_url: string) => {
    log("② new RTCPeerConnection()");
    const pc = new RTCPeerConnection();
    pcRef.current = pc;
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });
    pc.ontrack = (e) => {
      log(`   ↪ ontrack: kind=${e.track.kind}`);
      if (videoRef.current && e.streams[0]) {
        videoRef.current.srcObject = e.streams[0];
      }
    };
    pc.onconnectionstatechange = () =>
      log(`   ↪ connectionState=${pc.connectionState}`);
    pc.oniceconnectionstatechange = () =>
      log(`   ↪ iceConnectionState=${pc.iceConnectionState}`);

    // ③ createOffer + ICE-gathering wait. MediaMTX doesn't support trickle
    //    ICE on WHEP, so the offer must carry all candidates before POST.
    log("③ createOffer + setLocalDescription");
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    log("   ↪ waiting for ICE gathering…");
    await new Promise<void>((resolve) => {
      if (pc.iceGatheringState === "complete") return resolve();
      const onState = () => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", onState);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", onState);
      setTimeout(resolve, 3000);
    });

    // ④ POST the SDP offer, retrying through the publish-on-demand cold
    //    start (~3-8s for the connector to start publishing a camera nobody
    //    was watching). MediaMTX 404s until the path is ready.
    for (let attempt = 1; attempt <= START_RETRIES; attempt++) {
      log(attempt === 1 ? "④ POST WHEP offer" : `④ POST WHEP offer (retry ${attempt}/${START_RETRIES})`);
      const whepResp = await fetch(
        `${whep_url}?jwt=${encodeURIComponent(tokenRef.current)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/sdp" },
          body: pc.localDescription!.sdp,
        }
      );
      if (whepResp.ok) {
        const answerSdp = await whepResp.text();
        log("⑤ setRemoteDescription(answer)");
        await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
        log("   ↪ awaiting media…");
        return;
      }
      const body = await whepResp.text();
      if (whepResp.status === 404 && attempt < START_RETRIES) {
        log(`   ↪ starting stream… (camera not publishing yet, ~${START_RETRY_DELAY_MS / 1000}s)`);
        await sleep(START_RETRY_DELAY_MS);
        continue;
      }
      log(`   ✗ ${whepResp.status}: ${body}`);
      return;
    }
  };

  const playHls = async (hls_url: string, lookbackSeconds: number) => {
    if (!videoRef.current) return;
    // Lazy-load hls.js so a missing dep degrades gracefully (Safari native
    // can fall back, but it flakes on MediaMTX's 302 session redirects —
    // see the local comparison sandbox lessons in EDOCS).
    log("② import hls.js");
    const Hls = (await import("hls.js")).default;
    if (!Hls.isSupported()) {
      if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native fallback. Known to flake on MediaMTX 302 session
        // redirects, and it can't pick up re-minted tokens — long views
        // will die at JWT expiry on this path.
        log("③ falling back to Safari native HLS (may flake on 302s)");
        videoRef.current.src = `${hls_url}?jwt=${encodeURIComponent(tokenRef.current)}`;
        videoRef.current.play().catch((e) => log(`   ✗ play(): ${e}`));
        startBufferStats();
      } else {
        log("   ✗ no HLS support in this browser");
      }
      return;
    }

    const startAttempt = (attempt: number) => {
      if (!videoRef.current) return;
      log(
        attempt === 1
          ? "③ attaching hls.js player"
          : `③ starting stream… (cold start, attempt ${attempt}/${START_RETRIES})`
      );
      const hls = new Hls({
        lowLatencyMode: true,
        // Client back-buffer for pause / scrub-back; depth is the
        // per-customer entitlement from the session response.
        backBufferLength: lookbackSeconds,
        // Birth-of-stream gap tolerance: with publish-on-demand, a cold
        // camera's stream is born when the viewer clicks Play, so rewinding
        // near the oldest buffered point can land on the ragged first
        // segments. Jump holes up to 1s (default 0.1) and keep nudging
        // instead of error-flooding; playback hops the gap and continues.
        maxBufferHole: 1.0,
        nudgeMaxRetry: 10,
        // Inject the CURRENT token on every manifest/segment request — the
        // keepalive re-mints it every ~4 min, so long views survive the
        // 5-min JWT TTL without reloading the source.
        xhrSetup: (xhr: any, url: string) => {
          try {
            const u = new URL(url);
            u.searchParams.set("jwt", tokenRef.current);
            xhr.open("GET", u.toString(), true);
          } catch {
            // Malformed URL — let hls.js open it unmodified.
          }
        },
      });
      hlsRef.current = hls;
      hls.on(Hls.Events.ERROR, (_evt: any, data: any) => {
        if (!data.fatal) {
          // De-dupe: identical non-fatal errors within 5s collapse into one
          // line with a repeat count.
          const key = `${data.type}/${data.details}`;
          const now = Date.now();
          const last = lastHlsErrRef.current;
          if (key === last.key && now - last.t < 5000) {
            last.n += 1;
            last.t = now;
            return;
          }
          if (last.n > 0) {
            log(`   ↪ (hls ${last.key} repeated ×${last.n} more)`);
          }
          lastHlsErrRef.current = { key, t: now, n: 0 };
          log(`   ↪ hls error: ${key}`);
          return;
        }
        // Fatal during startup is almost always the publish-on-demand cold
        // start (manifest 404 before the connector begins publishing).
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
        if (attempt < START_RETRIES) {
          retryTimerRef.current = setTimeout(
            () => startAttempt(attempt + 1),
            START_RETRY_DELAY_MS
          );
        } else {
          log(`   ✗ hls fatal: ${data.type} / ${data.details} (gave up after ${START_RETRIES} attempts)`);
        }
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        log("   ↪ manifest parsed; playing");
        videoRef.current?.play().catch((e) => log(`   ✗ play(): ${e}`));
      });
      hls.loadSource(`${hls_url}?jwt=${encodeURIComponent(tokenRef.current)}`);
      hls.attachMedia(videoRef.current);
      startBufferStats();
    };
    startAttempt(1);
  };

  const play = async () => {
    stop();
    setLogLines([]);
    setBusy(true);
    setNotice("");
    sessionStartRef.current = Date.now();
    meterTicksRef.current = 0;
    try {
      log(`① POST /monitoring-api/streaming/stream-session/${cameraId}/`);
      const data = await mintSession(cameraId);
      const lookbackSeconds = data.lookback_seconds ?? 120;
      log(
        `   → ${protocol === "whep" ? `whep_url=${data.whep_url}` : `hls_url=${data.hls_url}`}`
      );
      log(
        `   → token=${data.token.slice(0, 30)}…  session=${
          data.session_id ?? "-"
        }  lookback=${lookbackSeconds}s  (expires in ${
          data.expires_at - Math.floor(Date.now() / 1000)
        }s; keepalive every ${KEEPALIVE_MS / 60000}min)`
      );
      startKeepalive(cameraId);

      if (protocol === "whep") {
        await playWhep(data.whep_url);
      } else {
        await playHls(data.hls_url, lookbackSeconds);
      }
    } catch (err: any) {
      const detail = err?.response?.data
        ? `${err.response.status}: ${JSON.stringify(err.response.data)}`
        : err?.message ?? String(err);
      log(`✗ ${detail}`);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    play();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  return (
    <div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>
      <h2 style={{ marginTop: 0 }}>Stream Smoke Test</h2>
      <p style={{ color: "#555", marginTop: 0 }}>
        Synthetic <code>testpattern</code> source is open to any logged-in
        user; real camera_ids resolve to a Camera row and are gated by
        the operator&rsquo;s Group access to the camera&rsquo;s customer.
        Real cameras publish on demand — first open takes a few seconds.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <label>
          camera_id:&nbsp;
          <input
            value={cameraId}
            onChange={(e) => setCameraId(e.target.value)}
            disabled={busy}
            style={{ padding: 4, width: 240 }}
          />
        </label>
        <label>
          protocol:&nbsp;
          <select
            value={protocol}
            onChange={(e) => setProtocol(e.target.value as Protocol)}
            disabled={busy}
            style={{ padding: 4 }}
          >
            <option value="whep">WHEP (WebRTC, &lt;1s)</option>
            <option value="hls">LL-HLS (~2-3s, firewall-friendly)</option>
          </select>
        </label>
        <button onClick={play} disabled={busy}>
          Play
        </button>
        <button onClick={stop}>Stop</button>
      </div>

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        controls
        style={{
          display: "block",
          width: 720,
          maxWidth: "100%",
          background: "#000",
          marginBottom: 8,
        }}
      />

      {notice && (
        <div
          style={{
            background: "#fff7e0",
            border: "1px solid #e0c860",
            padding: "6px 10px",
            fontSize: 13,
            marginBottom: 8,
          }}
        >
          {notice}
        </div>
      )}

      {bufInfo && (
        <div
          style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 12,
            color: "#333",
            marginBottom: 12,
          }}
        >
          {bufInfo}
        </div>
      )}

      <pre
        style={{
          background: "#f4f4f4",
          padding: 12,
          maxHeight: 360,
          overflow: "auto",
          fontSize: 12,
        }}
      >
        {logLines.join("\n") || "(idle)"}
      </pre>
    </div>
  );
};
